import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon 
} from '@heroicons/react/outline';

// Solution interface
interface Solution {
  id: string;
  title: string;
  tier: 'bronze' | 'silver' | 'gold';
  challenges: string[];
  sectors: string[];
}

const SolutionsPage: React.FC = () => {
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [solutionToDelete, setSolutionToDelete] = useState<string | null>(null);

  // Fetch solutions on component mount
  useEffect(() => {
    fetchSolutions();
  }, []);

  const fetchSolutions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/solutions');
      
      if (response.data.success) {
        setSolutions(response.data.data);
      } else {
        setError('Failed to load solutions');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while fetching solutions'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setSolutionToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!solutionToDelete) return;

    try {
      setLoading(true);
      const response = await axios.delete(`/api/solutions/${solutionToDelete}`);
      
      if (response.data.success) {
        // Remove deleted solution from state
        setSolutions((prevSolutions) => 
          prevSolutions.filter(solution => solution.id !== solutionToDelete)
        );
        
        // Close modal
        setShowDeleteModal(false);
        setSolutionToDelete(null);
      } else {
        setError('Failed to delete solution');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while deleting the solution'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSolutionToDelete(null);
  };

  // Get tier badge color
  const getTierBadgeClass = (tier: string) => {
    switch (tier) {
      case 'bronze':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'silver':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-muted text-muted-foreground dark:bg-dark-muted dark:text-dark-muted-foreground';
    }
  };

  if (loading && solutions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-foreground dark:text-dark-foreground">
          Loading...
        </div>
      </div>
    );
  }

  if (error && solutions.length === 0) {
    return (
      <div className="bg-destructive/10 dark:bg-dark-destructive/10 p-6 rounded-lg">
        <h2 className="text-xl font-bold text-destructive dark:text-dark-destructive mb-2">
          Error
        </h2>
        <p className="text-destructive dark:text-dark-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground dark:text-dark-foreground">
          Solutions
        </h1>
        <Link
          to="/admin/solutions/new"
          className="bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 text-white px-4 py-2 rounded-md inline-flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Solution
        </Link>
      </div>

      {error && (
        <div className="bg-destructive/10 dark:bg-dark-destructive/10 p-4 rounded-lg">
          <p className="text-destructive dark:text-dark-destructive">{error}</p>
        </div>
      )}

      <div className="bg-card dark:bg-dark-card rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border dark:divide-dark-border">
            <thead className="bg-muted dark:bg-dark-muted">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Tier
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Challenges
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background dark:bg-dark-background divide-y divide-border dark:divide-dark-border">
              {solutions.length > 0 ? (
                solutions.map((solution) => (
                  <tr key={solution.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground dark:text-dark-foreground">
                      {solution.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground dark:text-dark-foreground">
                      {solution.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTierBadgeClass(solution.tier)}`}>
                        {solution.tier.charAt(0).toUpperCase() + solution.tier.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground dark:text-dark-foreground">
                      <div className="flex flex-wrap gap-1">
                        {solution.challenges.slice(0, 2).map((challenge, index) => (
                          <span key={index} className="bg-muted dark:bg-dark-muted px-2 py-1 rounded-full text-xs">
                            {challenge}
                          </span>
                        ))}
                        {solution.challenges.length > 2 && (
                          <span className="bg-muted dark:bg-dark-muted px-2 py-1 rounded-full text-xs">
                            +{solution.challenges.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <Link
                          to={`/admin/solutions/${solution.id}`}
                          className="text-primary hover:text-primary/80 dark:text-white dark:hover:text-white/80"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(solution.id)}
                          className="text-destructive hover:text-destructive/80 dark:text-dark-destructive dark:hover:text-dark-destructive/80"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-muted-foreground dark:text-dark-muted-foreground">
                    No solutions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-foreground/50 dark:bg-dark-foreground/50 flex items-center justify-center z-50">
          <div className="bg-card dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
            <h3 className="text-lg font-medium text-foreground dark:text-dark-foreground mb-3">
              Confirm Deletion
            </h3>
            <p className="text-muted-foreground dark:text-dark-muted-foreground mb-6">
              Are you sure you want to delete this solution? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-foreground dark:text-dark-foreground bg-muted dark:bg-dark-muted hover:bg-muted/80 dark:hover:bg-dark-muted/80 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-white bg-destructive hover:bg-destructive/90 dark:bg-dark-destructive dark:hover:bg-dark-destructive/90 rounded-md"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionsPage; 